/**
 * Shared exposure chart type utilities for report builder, PDF rendering, and API clients.
 *
 * Playground exposure types use snake_case API values. Report builder stores
 * chart types in kebab-case (e.g. "qc-asset-class" → API "qc_asset_class").
 *
 * Keep `STANDARD_EXPOSURE_API_TYPES` in sync with
 * `ani_api/app/api/v1/endpoints/widgets/exposures.py` → `STANDARD_EXPOSURE_TYPES`.
 */

/** Standard exposure types from the widgets API (snake_case). */
export const STANDARD_EXPOSURE_API_TYPES = [
  "asset_allocation",
  "geographic_combined",
  "equity_countries",
  "bond_countries",
  "equity_sectors",
  "fixed_income_sectors",
  "credit_quality",
  "maturity",
  "byob_asset_allocation",
  "detailed_asset_allocation",
  "equity_standardized_regions",
  "bond_standardized_regions",
  "combined_standardized_regions",
  "currency",
] as const;

/** Chart types that use exposure data but are not pie/donut charts. */
const EXPOSURE_BAR_CHART_TYPES = new Set(["asset-allocation-bar"]);

/**
 * Chart types that are never exposure pie charts (even if they contain hyphens).
 */
const NON_EXPOSURE_CHART_TYPES = new Set([
  "line",
  "area",
  "bar",
  "column",
  "scatter-chart",
  "category-chart",
  "lookthrough",
  "asset-allocation-list",
  "asset-allocation-bar",
  "platform-boxes",
  "risk-scale",
  "legend-only",
  "cashflow-projection",
  "cashflow-fan",
  "exposure-columns",
  "exposure-over-time",
  "correlation-matrix",
  "column-rotated",
  "simple-column",
  "clustered-column",
  "column-gradient",
  "donut",
  "geographic-pie",
  "geographic-donut",
]);

/** Legacy chart-type aliases that map to a non-obvious API exposure type. */
const CHART_TYPE_TO_API_EXPOSURE: Record<string, string> = {
  pie: "asset_allocation",
  "asset-allocation": "asset_allocation",
  "asset-allocation-bar": "asset_allocation",
  "detailed-asset-allocation": "detailed_asset_allocation",
  "equity-sectors": "equity_sectors",
  "equity-countries": "equity_countries",
  "equity-regions": "equity_standardized_regions",
  "equity-standardized-regions": "equity_standardized_regions",
  "fixed-income-sectors": "fixed_income_sectors",
  "bond-countries": "bond_countries",
  "bond-standardized-regions": "bond_standardized_regions",
  "credit-quality": "credit_quality",
  maturity: "maturity",
  currency: "currency",
  "geographic-combined": "geographic_combined",
  "combined-standardized-regions": "combined_standardized_regions",
  "byob-asset-allocation": "byob_asset_allocation",
};

const GENERIC_EXPOSURE_CHART_TYPES = new Set([
  "pie",
  "lookthrough",
  "asset-allocation-list",
]);

export function snakeToKebabCase(value: string): string {
  return value.replace(/_/g, "-");
}

export function kebabToSnakeCase(value: string): string {
  return value.replace(/-/g, "_");
}

export function formatExposureLabel(value: string): string {
  return value
    .split(/[-_]/)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
}

/** Standard exposure options for report builder dropdowns (kebab-case values). */
export function getStandardExposureChartOptions(): Array<{
  value: string;
  label: string;
}> {
  return STANDARD_EXPOSURE_API_TYPES.map((apiType) => {
    const value = snakeToKebabCase(apiType);
    return {
      value,
      label: formatExposureLabel(value),
    };
  });
}

/** Known pie/donut exposure chart types (standard + legacy aliases). */
export const KNOWN_EXPOSURE_PIE_CHART_TYPES = new Set([
  "pie",
  ...STANDARD_EXPOSURE_API_TYPES.map(snakeToKebabCase),
  "equity-regions",
]);

export function isExposureBarChartType(chartType?: string): boolean {
  return !!chartType && EXPOSURE_BAR_CHART_TYPES.has(chartType);
}

function isKnownExposureValue(
  chartType: string,
  knownExposureValues?: Iterable<string>,
): boolean {
  if (!knownExposureValues) {
    return false;
  }

  for (const value of knownExposureValues) {
    if (value === chartType) {
      return true;
    }
  }

  return false;
}

/**
 * Returns true when the chart uses the exposures widget API as a pie/donut chart.
 * Client-specific types must appear in settings.exposureType or knownExposureValues.
 */
export function isExposurePieChartType(
  chartType?: string,
  settings?: { exposureType?: string },
  knownExposureValues?: Iterable<string>,
): boolean {
  if (!chartType) {
    return false;
  }

  if (KNOWN_EXPOSURE_PIE_CHART_TYPES.has(chartType)) {
    return true;
  }

  if (NON_EXPOSURE_CHART_TYPES.has(chartType)) {
    return false;
  }

  if (settings?.exposureType && chartType === settings.exposureType) {
    return true;
  }

  if (isKnownExposureValue(chartType, knownExposureValues)) {
    return true;
  }

  return false;
}

/** Pie or bar charts that fetch data from /widgets/exposures. */
export function isExposureDataChartType(
  chartType?: string,
  settings?: { exposureType?: string },
  knownExposureValues?: Iterable<string>,
): boolean {
  if (!chartType) {
    return false;
  }

  return (
    isExposurePieChartType(chartType, settings, knownExposureValues) ||
    isExposureBarChartType(chartType)
  );
}

function resolveConfiguredExposureApiType(configuredType: string): string {
  return (
    CHART_TYPE_TO_API_EXPOSURE[configuredType] ??
    kebabToSnakeCase(configuredType)
  );
}

/**
 * Resolve the API exposure_type (snake_case) for a report chart.
 */
export const SETTINGS_DRIVEN_EXPOSURE_CHART_TYPES = new Set([
  "exposure-columns",
  "exposure-over-time",
]);

export function isSettingsDrivenExposureChartType(chartType?: string): boolean {
  return !!chartType && SETTINGS_DRIVEN_EXPOSURE_CHART_TYPES.has(chartType);
}

export function chartTypeToExposureApiType(
  chartType: string,
  settings?: { exposureType?: string },
): string {
  const configuredType = settings?.exposureType;

  if (
    configuredType &&
    SETTINGS_DRIVEN_EXPOSURE_CHART_TYPES.has(chartType)
  ) {
    return resolveConfiguredExposureApiType(configuredType);
  }

  if (configuredType) {
    if (
      GENERIC_EXPOSURE_CHART_TYPES.has(chartType) ||
      chartType === configuredType
    ) {
      return resolveConfiguredExposureApiType(configuredType);
    }
  }

  if (chartType === "lookthrough" || chartType === "asset-allocation-list") {
    return "asset_allocation";
  }

  const mapped = CHART_TYPE_TO_API_EXPOSURE[chartType];
  if (mapped) {
    return mapped;
  }

  return kebabToSnakeCase(chartType);
}
